const TOKEN_KEY = 'kt_portal_token'
const SESSION_KEY = 'kt_portal_session'

export type PortalSession = {
  accessToken: string
  expiresAtUtc: string
  tenantId: string
  tenantSlug: string
  tenantName: string
  userId: string
  email: string
  displayName: string
  role: 'Owner' | 'Staff' | string
}

export function getApiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? ''
}

export function resolveImageUrl(url?: string | null): string {
  if (!url) return ''
  const trimmed = url.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:')) {
    return trimmed.replace(/([^:]\/)\/+/g, '$1')
  }
  const base = getApiBase()
  const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  const combined = base ? `${base}${cleanPath}` : cleanPath
  return combined.replace(/([^:]\/)\/+/g, '$1')
}

export function loadSession(): PortalSession | null {
  const raw = localStorage.getItem(SESSION_KEY)
  if (!raw) return null
  try {
    const session = JSON.parse(raw) as PortalSession
    if (!session.accessToken || new Date(session.expiresAtUtc) <= new Date()) {
      clearSession()
      return null
    }
    return session
  } catch {
    clearSession()
    return null
  }
}

export function saveSession(session: PortalSession) {
  localStorage.setItem(TOKEN_KEY, session.accessToken)
  localStorage.setItem(SESSION_KEY, JSON.stringify(session))
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(SESSION_KEY)
}

export function getToken(): string | null {
  return loadSession()?.accessToken ?? null
}

export class ApiError extends Error {
  status: number
  body: unknown
  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.status = status
    this.body = body
  }
}

export async function api<T>(
  path: string,
  options: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(options.headers)
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json')
  }
  if (options.auth !== false) {
    const token = getToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
  }

  const res = await fetch(`${getApiBase()}${path}`, { ...options, headers })
  if (res.status === 204) return undefined as T

  const text = await res.text()
  let data: any = null
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = null
    }
  }

  if (!res.ok) {
    let message = data && data.message
    if (!message && data?.errors && typeof data.errors === 'object') {
      const firstKey = Object.keys(data.errors)[0]
      if (firstKey && Array.isArray(data.errors[firstKey]) && data.errors[firstKey].length > 0) {
        message = data.errors[firstKey][0]
      }
    }
    if (!message) {
      message = (data && data.title) || (text && text.length < 200 ? text : `Request failed (${res.status})`)
    }
    if (res.status === 401) clearSession()
    throw new ApiError(res.status, message, data)
  }
  return data as T
}

export type LoginResponse = {
  accessToken: string
  expiresAtUtc: string
  tenantId: string
  tenantSlug: string
  tenantName: string
  userId: string
  email: string
  displayName: string
  role: string
}

export async function login(tenantSlug: string, email: string, password: string) {
  const result = await api<LoginResponse>('/api/portal/auth/login', {
    method: 'POST',
    auth: false,
    body: JSON.stringify({ tenantSlug, email, password }),
  })
  saveSession(result)
  return result
}

export type PublicPlan = {
  id: string
  name: string
  description?: string | null
  basePrice: number
  currency: string
  usageLimitPerMonth?: number | null
  overagePricePerOrder?: number | null
  billingInterval: string
}

export async function fetchPublicPlans() {
  return api<PublicPlan[]>('/api/portal/plans', { auth: false })
}

export async function fetchPublicConfig() {
  return api<{ trialAutoApply: boolean }>('/api/portal/plans/config', { auth: false })
}

export type RegisterPayload = {
  businessName: string
  email: string
  password: string
  confirmPassword: string
  phoneNumber?: string
  storeName?: string
  slug?: string
  planId: string
}

export type RegisterResponse = LoginResponse & {
  sandboxApiKey: string
  keyWarning: string
}

export async function register(payload: RegisterPayload) {
  const result = await api<RegisterResponse>('/api/portal/auth/register', {
    method: 'POST',
    auth: false,
    body: JSON.stringify(payload),
  })
  const { sandboxApiKey: _k, keyWarning: _w, ...session } = result
  saveSession(session)
  return result
}

export function formatMoney(amount: number, currency = 'USD', fractionDigits = 0) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount)
  } catch {
    return `${currency} ${amount}`
  }
}

export type LiveOrderLineOption = {
  groupName?: string
  optionLabel?: string
  price?: number
  optionId?: string
}

export type LiveOrderLine = {
  id: string
  productName: string
  quantity: number
  unitPrice: number
  lineTotal: number
  options?: LiveOrderLineOption[] | null
}

export type LiveOrder = {
  id: string
  orderNumber: string
  customerName?: string
  customerPhone?: string
  customerNote?: string
  orderType?: string
  kitchenStatus: string
  acknowledged: boolean
  demoMode: boolean
  subtotal: number
  taxTotal: number
  tipAmount?: number
  grandTotal: number
  currency: string
  createdAt: string
  paidAt?: string | null
  totalItemQty: number
  lines: LiveOrderLine[]
  paymentStatus?: string
}

export async function fetchLiveOrders() {
  return api<LiveOrder[]>('/api/portal/orders/live')
}

export async function acknowledgeOrder(id: string) {
  return api<{ ok: boolean }>(`/api/portal/orders/${id}/acknowledge`, { method: 'POST' })
}

export async function markOrderReady(id: string) {
  return api<{ ok: boolean }>(`/api/portal/orders/${id}/ready`, { method: 'POST' })
}

export async function completeOrder(id: string) {
  return api<{ ok: boolean }>(`/api/portal/orders/${id}/complete`, { method: 'POST' })
}

export async function createDemoOrder() {
  return api<{ id: string; orderNumber: string }>('/api/portal/orders/demo', {
    method: 'POST',
  })
}

export type OrderHistoryFilters = {
  startDate?: string
  endDate?: string
  kitchenStatus?: string
  paymentStatus?: string
  searchQuery?: string
  page?: number
  pageSize?: number
}

export type OrderHistoryResponse = {
  totalCount: number
  page: number
  pageSize: number
  items: LiveOrder[]
}

export async function getOrderHistory(filters: OrderHistoryFilters = {}) {
  const params = new URLSearchParams()
  if (filters.startDate) params.set('startDate', filters.startDate)
  if (filters.endDate) params.set('endDate', filters.endDate)
  if (filters.kitchenStatus) params.set('kitchenStatus', filters.kitchenStatus)
  if (filters.paymentStatus) params.set('paymentStatus', filters.paymentStatus)
  if (filters.searchQuery) params.set('searchQuery', filters.searchQuery)
  if (filters.page) params.set('page', filters.page.toString())
  if (filters.pageSize) params.set('pageSize', filters.pageSize.toString())

  const qs = params.toString()
  const url = qs ? `/api/portal/orders/history?${qs}` : '/api/portal/orders/history'
  return api<OrderHistoryResponse>(url)
}

export type TrialStatus = {
  trialEndsAt: string | null
  trialOrdersRemaining: number | null
  isTrialActive: boolean
  hasExpiredTrial: boolean
  isTrialEndingSoon?: boolean
  billingBlocked?: boolean
  billingBlockedReason?: 'QUOTA_EXHAUSTED' | 'TRIAL_EXPIRED' | 'NO_ACTIVE_PLAN' | string | null
  canRequestTrial: boolean
  pendingRequest: boolean
  offerDurationMonths: number
  offerOrderQuota: number
}

export function getTrialStatus() {
  return api<TrialStatus>('/api/portal/billing/trial-status')
}

export function requestTrial() {
  return api<{ ok: boolean }>('/api/portal/billing/request-trial', { method: 'POST' })
}

export type DashboardStats = {
  ordersThisMonth: number
  revenueThisMonth: number
  ordersLast7Days: { date: string; count: number }[]
  revenueLast7Days: { date: string; amount: number }[]
  recentOrders: LiveOrder[]
  subscription: {
    status: string
    planName: string | null
    nextBillingDate: string | null
    usageThisPeriod: number
    usageLimitPerMonth: number | null
    overagePricePerOrder: number
    currency: string
  } | null
  trial: TrialStatus | null
}

export function getDashboardStats() {
  return api<DashboardStats>('/api/portal/billing/dashboard-stats')
}

/* ——— Catalog ——— */

export type Category = {
  id: string
  name: string
  sortOrder: number
  isActive: boolean
  imageUrl?: string | null
}

export type CategoryBody = {
  name: string
  sortOrder: number
  isActive: boolean
  imageUrl?: string | null
}

export async function fetchCategories() {
  return api<Category[]>('/api/portal/categories')
}

export async function createCategory(body: CategoryBody) {
  return api<{ id: string }>('/api/portal/categories', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function updateCategory(id: string, body: CategoryBody) {
  return api<{ ok: boolean }>(`/api/portal/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export async function deleteCategory(id: string) {
  return api<{ ok: boolean }>(`/api/portal/categories/${id}`, { method: 'DELETE' })
}

export type ProductListItem = {
  id: string
  categoryId?: string | null
  name: string
  price: number
  cost?: number | null
  isActive: boolean
  isSoldOut: boolean
  requiresCustomization: boolean
  sortOrder: number
  imageUrl?: string | null
}

export type AddonOption = {
  id?: string
  label: string
  priceDelta: number
  costDelta?: number | null
  sortOrder: number
  isActive: boolean
  imageUrl?: string | null
}

export type AddonGroup = {
  id?: string
  name: string
  description?: string | null
  type: string
  isRequired: boolean
  minSelections: number
  maxSelections?: number | null
  sortOrder: number
  isActive: boolean
  options: AddonOption[]
}

export type ProductDetail = {
  id: string
  categoryId?: string | null
  name: string
  description?: string | null
  price: number
  cost?: number | null
  taxRate: number
  taxInclusive: boolean
  useGlobalTax: boolean
  isActive: boolean
  isSoldOut: boolean
  sortOrder: number
  requiresCustomization: boolean
  images: { id: string; url: string; sortOrder: number }[]
  addons: AddonGroup[]
}

export type ProductBody = {
  categoryId?: string | null
  name: string
  description?: string | null
  price: number
  cost?: number | null
  taxRate: number
  taxInclusive: boolean
  useGlobalTax: boolean
  isActive: boolean
  isSoldOut: boolean
  sortOrder: number
}

export async function fetchProducts(params?: { categoryId?: string; q?: string; status?: 'active' | 'inactive' | 'all' }) {
  const qs = new URLSearchParams()
  if (params?.categoryId) qs.set('categoryId', params.categoryId)
  if (params?.q) qs.set('q', params.q)
  if (params?.status) qs.set('status', params.status)
  const suffix = qs.toString() ? `?${qs}` : ''
  return api<ProductListItem[]>(`/api/portal/products${suffix}`)
}

export async function fetchProduct(id: string) {
  return api<ProductDetail>(`/api/portal/products/${id}`)
}

export async function createProduct(body: ProductBody) {
  return api<{ id: string }>('/api/portal/products', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function updateProduct(id: string, body: ProductBody) {
  return api<{ ok: boolean }>(`/api/portal/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export async function deleteProduct(id: string) {
  return api<{ ok: boolean }>(`/api/portal/products/${id}`, { method: 'DELETE' })
}

export async function bulkDeleteProducts(ids: string[]) {
  return api<{ count: number }>('/api/portal/products/bulk-delete', {
    method: 'POST',
    body: JSON.stringify(ids),
  })
}

export async function restoreProduct(id: string) {
  return api<{ ok: boolean }>(`/api/portal/products/${id}/restore`, { method: 'POST' })
}

export async function bulkRestoreProducts(ids: string[]) {
  return api<{ count: number }>('/api/portal/products/bulk-restore', {
    method: 'POST',
    body: JSON.stringify(ids),
  })
}

export async function addProductImage(id: string, url: string, sortOrder = 0) {
  return api<{ id: string }>(`/api/portal/products/${id}/images`, {
    method: 'POST',
    body: JSON.stringify({ url, sortOrder }),
  })
}

export async function uploadImage(file: File) {
  const formData = new FormData()
  formData.append('file', file)
  
  const token = getToken()
  const res = await fetch(`${getApiBase()}/api/portal/products/upload/image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: formData,
  })

  if (!res.ok) {
    const text = await res.text()
    try {
      const data = JSON.parse(text)
      throw new Error(data.message || `Upload failed (${res.status})`)
    } catch {
      throw new Error(`Upload failed (${res.status})`)
    }
  }

  const data = (await res.json()) as { url: string }
  let cleanUrl = data.url ?? ''
  if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
    cleanUrl = cleanUrl.replace(/([^:]\/)\/+/g, '$1')
  } else {
    cleanUrl = cleanUrl.replace(/\/+/g, '/')
  }
  return { url: cleanUrl }
}

export async function deleteProductImage(id: string, imageId: string) {
  return api<{ ok: boolean }>(`/api/portal/products/${id}/images/${imageId}`, {
    method: 'DELETE',
  })
}

export async function saveProductAddons(id: string, groups: AddonGroup[]) {
  return api<{ ok: boolean; requiresCustomization: boolean }>(
    `/api/portal/products/${id}/addons`,
    {
      method: 'PUT',
      body: JSON.stringify(groups),
    },
  )
}

/* ——— Tax Settings ——— */

export type TaxSettings = {
  defaultTaxRate: number
  defaultTaxInclusive: boolean
}

export async function fetchTaxSettings() {
  return api<TaxSettings>('/api/portal/tax')
}

export async function saveTaxSettings(body: TaxSettings) {
  return api<TaxSettings>('/api/portal/tax', {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

/* ——— Tip Settings ——— */

export type TipSettings = {
  isEnabled: boolean
  percentages: number[]
  defaultPercentage: number
  allowCustom: boolean
}

export async function fetchTipSettings() {
  return api<TipSettings>('/api/portal/tips')
}

export async function saveTipSettings(body: TipSettings) {
  return api<TipSettings>('/api/portal/tips', {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

/* ——— Staff ——— */

export type PortalUser = {
  id: string
  email: string
  displayName?: string | null
  role: string
  isActive: boolean
}

export async function fetchUsers() {
  return api<PortalUser[]>('/api/portal/users')
}

export async function createStaffUser(body: {
  email: string
  password: string
  displayName?: string
}) {
  return api<{ id: string }>('/api/portal/users', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function updateStaffUser(
  id: string,
  body: { displayName?: string; isActive?: boolean; password?: string },
) {
  return api<{ ok: boolean }>(`/api/portal/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export async function deactivateStaffUser(id: string) {
  return api<{ ok: boolean }>(`/api/portal/users/${id}`, { method: 'DELETE' })
}

/* ——— Keys ——— */

export type ApiKeyRow = {
  id: string
  prefix: string
  environment: string
  isActive: boolean
  createdAt: string
  lastUsedAt?: string | null
}

export async function fetchApiKeys() {
  return api<ApiKeyRow[]>('/api/portal/keys')
}

export async function createApiKey(environment: 'Sandbox' | 'Live') {
  return api<{ id: string; environment: string; prefix: string; apiKey: string }>(
    '/api/portal/keys',
    { method: 'POST', body: JSON.stringify({ environment }) },
  )
}

export async function revokeApiKey(id: string) {
  return api<{ ok: boolean }>(`/api/portal/keys/${id}/revoke`, { method: 'POST' })
}

/* ——— Theme ——— */

export interface ThemeSettings {
  themeId: number
  colorSchemeId: number
  roundedCorners?: boolean
  standbyImageUrl1?: string | null
  standbyImageUrl2?: string | null
  standbyImageUrl3?: string | null
  standbyTimeoutSeconds?: number
  updatedAt: string
}

export async function fetchTheme() {
  return api<ThemeSettings>('/api/portal/theme')
}

export async function saveTheme(theme: Partial<ThemeSettings>) {
  return api<{ ok: boolean }>('/api/portal/theme', {
    method: 'PUT',
    body: JSON.stringify(theme)
  })
}

export async function uploadThemeImage(file: File) {
  const formData = new FormData()
  formData.append('file', file)
  
  const token = getToken()
  const res = await fetch(`${getApiBase()}/api/portal/theme/upload/image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: formData,
  })

  if (!res.ok) {
    const text = await res.text()
    try {
      const data = JSON.parse(text)
      throw new Error(data.message || `Upload failed (${res.status})`)
    } catch {
      throw new Error(`Upload failed (${res.status})`)
    }
  }

  const data = (await res.json()) as { url: string }
  let cleanUrl = data.url ?? ''
  if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
    cleanUrl = cleanUrl.replace(/([^:]\/)\/+/g, '$1')
  } else {
    cleanUrl = cleanUrl.replace(/\/+/g, '/')
  }
  return { url: cleanUrl }
}

/* ——— Settings ——— */

export type PortalSettings = {
  storeName: string
  email: string
  phoneNumber?: string | null
  kioskDisplayName?: string | null
  squareSetup: string[]
}

export async function getSetupStatus(): Promise<{ portalSetupDone: boolean }> {
  return await api<{ portalSetupDone: boolean }>('/api/portal/setup/status')
}

// --- SQUARE INTEGRATION ---
export type SquareSettings = {
  isConnected: boolean
  merchantId?: string
  locationId?: string
  autoSyncEnabled: boolean
  syncIntervalMinutes: number
  lastSyncAt?: string
  lastSyncStatus?: string
  lastSyncError?: string
}

export async function getSquareAuthUrl(): Promise<{ url: string }> {
  return await api<{ url: string }>('/api/portal/square/auth-url')
}

export async function submitSquareCallback(code: string): Promise<{ ok: boolean }> {
  return await api<{ ok: boolean }>('/api/portal/square/callback', {
    method: 'POST',
    body: JSON.stringify({ code })
  })
}

export async function getSquareSettings(): Promise<SquareSettings> {
  return await api<SquareSettings>('/api/portal/square/settings')
}

export async function updateSquareSettings(data: { locationId?: string, autoSyncEnabled: boolean, syncIntervalMinutes: number }): Promise<{ ok: boolean }> {
  return await api<{ ok: boolean }>('/api/portal/square/settings', {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

export async function triggerSquareSync(): Promise<{ ok: boolean, message: string }> {
  return await api<{ ok: boolean, message: string }>('/api/portal/square/sync', {
    method: 'POST'
  })
}

export async function pushSquareCatalog(): Promise<{ ok: boolean, message: string, categoriesPushed?: number, productsPushed?: number }> {
  return await api<{ ok: boolean, message: string, categoriesPushed?: number, productsPushed?: number }>('/api/portal/square/push', {
    method: 'POST'
  })
}

export async function getSquareLocations(): Promise<Array<{ id: string, name: string, status: string }>> {
  return await api<Array<{ id: string, name: string, status: string }>>('/api/portal/square/locations')
}

export async function fetchSettings() {
  return api<PortalSettings>('/api/portal/settings')
}

export async function saveSettings(body: {
  storeName: string
  phoneNumber?: string | null
  kioskDisplayName?: string | null
}) {
  return api<{ ok: boolean }>('/api/portal/settings', {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

/* ——— Billing ——— */

export type BillingSubscription = {
  subscriptionId?: string
  status?: string
  nextBillingDate?: string
  plan?: {
    id: string
    name: string
    basePrice: number
    currency: string
    usageLimitPerMonth?: number | null
    overagePricePerOrder?: number | null
    billingInterval?: string | null
  } | null
  usageThisPeriod?: number
  subscription?: null
}

export type BillingInvoice = {
  id: string
  periodStart: string
  periodEnd: string
  amountDue: number
  status: string
  createdAt: string
}

export type BillingConfig = {
  mode?: string
  publishableKey?: string | null
}

export async function fetchBillingSubscription() {
  return api<BillingSubscription>('/api/portal/billing/subscription')
}

export async function fetchBillingInvoices() {
  return api<BillingInvoice[]>('/api/portal/billing/invoices')
}

export async function fetchBillingConfig() {
  return api<BillingConfig>('/api/portal/billing/config')
}

export async function payInvoice(invoiceId: string, successUrl: string, cancelUrl: string) {
  return api<{ url?: string; checkoutUrl?: string }>('/api/portal/billing/pay', {
    method: 'POST',
    body: JSON.stringify({ invoiceId, successUrl, cancelUrl }),
  })
}

export type BillingPlan = {
  id: string
  name: string
  description?: string | null
  basePrice: number
  currency: string
  usageLimitPerMonth?: number | null
  overagePricePerOrder?: number | null
  billingInterval: string
}

export async function fetchBillingPlans() {
  return api<BillingPlan[]>('/api/portal/billing/plans')
}

export async function changeBillingPlan(planId: string) {
  return api<{ ok: boolean; planName: string; basePrice: number; currency: string; billingInterval: string; hasStripeSubscription?: boolean }>(
    '/api/portal/billing/change-plan',
    {
      method: 'POST',
      body: JSON.stringify({ planId }),
    }
  )
}

export async function subscribeToPlan(planId: string, successUrl: string, cancelUrl: string) {
  return api<{ checkoutUrl?: string; url?: string; sessionId?: string }>('/api/portal/billing/subscribe', {
    method: 'POST',
    body: JSON.stringify({ planId, successUrl, cancelUrl }),
  })
}

export async function openCustomerPortalSession(returnUrl?: string) {
  return api<{ url: string }>('/api/portal/billing/portal-session', {
    method: 'POST',
    body: JSON.stringify({ returnUrl }),
  })
}

